import { useEffect } from 'react'

const HOTMART_SCRIPT_URL =
  'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js'

export function useHotmartSalesFunnel(mountId: string) {
  useEffect(() => {
    let mounted = true
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let pollTimeout: ReturnType<typeof setTimeout> | null = null

    const initWidget = () => {
      if (!mounted) return false
      const el = document.getElementById(mountId)
      if (!el) return false
      const ce = (window as any).checkoutElements
      if (!ce) return false
      try {
        // Clear any previous widget content so it can re-mount cleanly
        el.innerHTML = ''
        ce.init('salesFunnel').mount(`#${mountId}`)
        return true
      } catch (e) {
        console.warn('[Hotmart] Widget mount error:', e)
        return false
      }
    }

    const waitAndInit = () => {
      // Try immediately first
      if (initWidget()) return

      // Poll every 300ms until checkoutElements is available
      pollTimer = setInterval(() => {
        if (initWidget() && pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
      }, 300)

      // Stop polling after 15 seconds to avoid infinite loops
      pollTimeout = setTimeout(() => {
        if (pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
      }, 15000)
    }

    const existingScript = document.querySelector(
      `script[src="${HOTMART_SCRIPT_URL}"]`,
    )

    if (existingScript) {
      // Script tag exists — it may or may not be loaded
      if ((window as any).checkoutElements) {
        // Already loaded — small delay to ensure React has rendered the mount element
        const t = setTimeout(() => waitAndInit(), 50)
        return () => {
          mounted = false
          clearTimeout(t)
          if (pollTimer) clearInterval(pollTimer)
          if (pollTimeout) clearTimeout(pollTimeout)
          const el = document.getElementById(mountId)
          if (el) el.innerHTML = ''
        }
      } else {
        // Script tag exists but not loaded yet
        const onLoad = () => waitAndInit()
        existingScript.addEventListener('load', onLoad)
        // Also start polling as fallback (script might have loaded between our check and addEventListener)
        waitAndInit()
        return () => {
          mounted = false
          existingScript.removeEventListener('load', onLoad)
          if (pollTimer) clearInterval(pollTimer)
          if (pollTimeout) clearTimeout(pollTimeout)
          const el = document.getElementById(mountId)
          if (el) el.innerHTML = ''
        }
      }
    }

    // No script tag yet — create and append
    const script = document.createElement('script')
    script.src = HOTMART_SCRIPT_URL
    script.async = true
    script.onload = () => waitAndInit()
    document.body.appendChild(script)

    return () => {
      mounted = false
      if (pollTimer) clearInterval(pollTimer)
      if (pollTimeout) clearTimeout(pollTimeout)
      const el = document.getElementById(mountId)
      if (el) el.innerHTML = ''
    }
  }, [mountId])
}
